import { captureStorageSession } from "../../services/storage";
import { cacheRemoteAttendees, completeOpenHouse } from "../../services/attendeeSubmission";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "react-query";
import ManualSyncButton from "../../components/ManualSyncButton";
import PageHeader from "../../components/PageHeader";
import { View, Text, Button } from "../../components/Themed";
import { useGetPingTime } from "../../hooks/useGetPingTime";
import BackButton from "./components/BackButton";
import GlobalContext from "../../context/Global";
import {
  PageTitleAddress,
  PageTitleContainer,
  PageTitleDate,
} from "./components/PageTitleContainer";
import {
  Platform,
  Pressable,
  AppState,
  ActivityIndicator,
  Modal,
} from "react-native";
import MoreOptionsButton from "./components/MoreOptionsButton";
import MoreOptionsModal, {
  MoreOptionsModalOption,
  MoreOptionsModalRef,
} from "../../components/MoreOptionsModal";
import Toast from "react-native-toast-message";
import { OpenHouse, Orientation } from "../../types";
import { ClosedData } from "../../types/OpenHouse";
import Constants from "expo-constants";
import * as Clipboard from "expo-clipboard";
import getDeviceInfo from "../../utils/deviceInfo";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import {
  OpenHouseInformation,
  OpenHouseInformationButtonContainer,
  OpenHouseInformationLabel,
  OpenHouseInformationTopContainer,
  OpenHouseInformationTopSection,
  OpenHouseNotes,
  OpenHouseTags,
} from "./components/InfoContainer";
import Line from "../../components/Line";
import { useGetFormQuestions } from "../../hooks/useGetFormQuestions";
import {
  getLocalData,
  removeKey,
  storeLocalData,
} from "../../services/storage";
import Colors from "../../constants/Colors";
import openhouseAPI from "../../services/openhouse";
import queryClient from "../../services/reactQuery";
import {
  ListHeader,
  ListHeaderSection,
  ListSection,
  MainSection,
  StatSection,
} from "./components/MainSection";
import StatsBlock from "../../components/StatBlock";
import { calculateHoursBetween } from "../../utils/CalculateHoursBetween";
import { useGetOpenHouseDetails } from "../../hooks/useGetOpenHouseDetails";
import { useDownloadSignIn } from "../../hooks/useDownloadSignIn";
import { LeadList } from "./components/LeadList";
import { RefreshButton } from "./components/RefreshButton";
import CreateEditOpenHouse from "../../modals/CreateEditOpenHouse";
import Popup from "../../components/Popup";
import useDeleteOpenHouse from "../../hooks/useDeleteOpenHouse";
import { useFocusEffect } from "@react-navigation/native";
import { useSyncUnsyncedAttendees } from "../../hooks/useSyncUnSyncedAttendees";
import useLocation from "../../hooks/useLocation";

import { getNavigationPrefix } from "../../utils/urls";

const NAVIGATION_PREFIX = getNavigationPrefix();

const OpenHouseDetails = ({ navigation, route }: any) => {
  const { openHouse: _openHouse } = route.params;
  const { data: pendingCompletions } = useQuery(
    ["syncData", _openHouse?.uuid],
    async () => (await getLocalData("syncData")) || []
  );
  const completionPending = pendingCompletions?.some(
    (item: any) => item.data?.uuid === _openHouse?.uuid
  );
  const { isTablet, user } = useContext(GlobalContext);
  const { getCurrentLocation } = useLocation();

  const moreOptionsModalRef = useRef<MoreOptionsModalRef>(null);

  const [attendees, setAttendees] = useState<any[]>([]);
  const [localAttendees, setLocalAttendees] = useState<boolean | null>(null);
  const [openHouse, setOpenHouse] = useState<OpenHouse>(_openHouse);
  const [showOpenHouseModal, setShowOpenHouseModal] = useState(false);
  const [showOpenHouseUpdatedModal, setShowOpenHouseUpdatedModal] =
    useState(false);
  const [showConfirmDeleteOpenHouseModal, setShowConfirmDeleteOpenHouseModal] =
    useState(false);
  const [showCannotDeleteModal, setShowCannotDeleteModal] = useState(false);
  const [showNoConnectionModal, setShowNoConnectionModal] = useState(false);
  const [showOfflineCompleteModal, setShowOfflineCompleteModal] =
    useState(false);
  const [pingTime, setPingTime] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [appStateVisible, setAppStateVisible] = useState<string | null>(null);
  const [showDownloadLoading, setShowDownloadLoading] = useState(false);
  const [formType, setFormType] = useState<string>("");
  const {
    data: attendeesAndTags,
    refetch: refetchAttendees,
    isFetching,
    isFetched,
  } = useGetOpenHouseDetails(_openHouse?.uuid);
  const _deleteOpenHouse = useDeleteOpenHouse();
  const syncAttendees = useSyncUnsyncedAttendees();
  const downloadSignInMutation = useDownloadSignIn();

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      setAppStateVisible(nextAppState);
    });

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (appStateVisible === "active") {
      getPing();
    }
  }, [appStateVisible]);

  useEffect(() => {
    const loadFormType = async () => {
      const q = await getLocalData(
        `@formQuestions+${route?.params?.openHouse?.form_uuid}`
      );
      const questions = q || [];
      setFormType(questions.length > 1 ? "Full Registration" : "Light Registration");
    };
    loadFormType();
  }, [route?.params?.openHouse?.form_uuid]);

  const getPing = async () => {
    const pingTime = await useGetPingTime();
    setPingTime(pingTime);
  };

  useEffect(() => {
    getPing();
  }, []);

  useFocusEffect(
    useCallback(() => {
      const fetchAttendees = async () => {
        if (_openHouse?.uuid) {
          await getAllAttendees(_openHouse?.uuid);
          getPing();
        }
      };
      fetchAttendees();
    }, [_openHouse?.uuid])
  );

  useFocusEffect(
    useCallback(() => {
      if (route?.params?.openHouse?.uuid && !isFetching) {
        _sync(route?.params?.openHouse?.uuid);
      }
    }, [route?.params?.openHouse?.uuid, attendeesAndTags?.attendees])
  );

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const debouncedSync = (uuid: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      _sync(uuid);
    }, 3500);
  };

  useFocusEffect(
    useCallback(() => {
      const uuid = route.params.openHouse.uuid;
      debouncedSync(uuid);

      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }, [route.params.openHouse.uuid])
  );

  const _sync = async (uuid: string) => {
    const pingTime = await useGetPingTime();
    if (pingTime === null) return;
    setSyncing(true);
    await syncAttendees(uuid);
    await getAllAttendees(uuid);
    setSyncing(false);
  };

  useEffect(() => {
    if (isFetched) {
      getAllAttendees(_openHouse?.uuid);
    }
  }, [isFetched]);

  useEffect(() => {
    if (attendeesAndTags?.attendees) {
      setAttendees(attendeesAndTags.attendees);
    }
  }, [attendeesAndTags?.attendees]);

  const getAllAttendees = async (uuid: string) => {
    const { assertAccount, getLocalData, isCurrent } = captureStorageSession();
    try {
      let remoteData = attendeesAndTags;
      if (pingTime !== null) {
        const refreshed = await refetchAttendees();
        if (refreshed.error) throw refreshed.error;
        remoteData = refreshed.data;
      }

      const storedAttendees = await getLocalData(`@openHouse+${uuid}`);
      // Confirm an empty snapshot before pruning previously confirmed records.
      if (pingTime !== null && Array.isArray(remoteData?.attendees) &&
          remoteData.attendees.length === 0 && storedAttendees?.some((entry: any) => entry.uuid)) {
        const confirmed = await refetchAttendees();
        if (confirmed.error) throw confirmed.error;
        if (!Array.isArray(confirmed.data?.attendees)) throw new Error("Unable to verify attendee list");
        remoteData = confirmed.data;
      }

      if (pingTime !== null && !remoteData?.attendees) {
        return;
      }

      const remoteAttendees = remoteData?.attendees || [];
      let finalAttendees;
      let isLocal = false;

      if (pingTime !== null && Array.isArray(remoteData?.attendees)) {
        if (storedAttendees) {
          const unsynedStoredAttendees = storedAttendees.filter(
            (local: any) => !local.is_synced && !local.uuid
          );
          const syncedRemoteAttendees = remoteAttendees.map((attendee) => ({
            ...attendee,
            is_synced: true,
          }));
          finalAttendees = [
            ...unsynedStoredAttendees,
            ...syncedRemoteAttendees,
          ];
          isLocal = true;
        } else {
          finalAttendees = remoteAttendees.map((attendee) => ({
            ...attendee,
            is_synced: true,
          }));
          isLocal = false;
        }
        assertAccount();
        await cacheRemoteAttendees(uuid, remoteAttendees);
      } else if (storedAttendees) {
        finalAttendees = storedAttendees;
        isLocal = true;
      } else {
        finalAttendees = [];
        isLocal = false;
      }

      setLocalAttendees(isLocal);
      setAttendees(finalAttendees);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching attendees:", error);
      const storedAttendees = await getLocalData(`@openHouse+${uuid}`);
      setLocalAttendees(!!storedAttendees);
      setAttendees(storedAttendees || []);
    }
  };

  const closeOpenHouse = async (closedData: ClosedData) => {
    const { assertAccount, getLocalData, storeLocalData, removeKey } = captureStorageSession();
    // Try to get user's current location (non-blocking - will continue even if it fails)
    let completedLatitude: number | null = null;
    let completedLongitude: number | null = null;

    try {
      const location = await getCurrentLocation();
      if (location) {
        completedLatitude = location.latitude;
        completedLongitude = location.longitude;
      }
    } catch (error) {
      // Silently fail - location is optional, don't block completing the open house
      console.log("Could not get location for open house completion:", error);
    }

    const deviceInfo = await getDeviceInfo();

    assertAccount();
    const _attendees = attendees
      .filter((attendee) => attendee.is_synced === undefined)
      .map(({ local_submission_id, delivery_baseline_ids, is_synced, ...attendee }) => attendee);
    const data = {
      uuid: openHouse.uuid,
      manualAttendeeCount: closedData.attendeeCount,
      attendees: _attendees,
      primaryAgentUserId: user?.id,
      endTime: closedData?.endTime.toLocaleTimeString("en-US", {
        hour12: false,
      }),
      startTime: closedData?.startTime?.toLocaleTimeString("en-US", {
        hour12: false,
      }),
      startDate: closedData?.startTime.toISOString(),
      instructions: closedData?.notes,
      deviceInfo,
    };
    if(completedLatitude !== null && completedLongitude !== null) {
      data.completedLatitude = completedLatitude;
      data.completedLongitude = completedLongitude;
    }
    
    if (pingTime === null) {
      try {
        let syncData = await getLocalData(`syncData`);
        const localStarted = await getLocalData(`@startedUuids`);
        if (localStarted) {
          const startedIndex = localStarted.findIndex(
            (startedItem) => startedItem.uuid === openHouse.uuid
          );

          if (startedIndex !== -1) {
            localStarted[startedIndex] = {
              ...localStarted[startedIndex],
              completed_at: new Date().toISOString(),
            };
          }
          await storeLocalData(`@startedUuids`, localStarted);
        }
        (openHouse.end_time = closedData?.endTime.toLocaleTimeString("en-US", {
          hour12: false,
        })),
          (openHouse.start_time = closedData?.startTime?.toLocaleTimeString(
            "en-US",
            {
              hour12: false,
            }
          )),
          (openHouse.completed_at = new Date().toISOString());
        openHouse.is_active = false;
        openHouse.manual_attendee_count = closedData.attendeeCount;
        openHouse.instructions = closedData.notes;
        // Store location in offline mode if available
        if (completedLatitude !== null && completedLongitude !== null) {
          openHouse.completed_latitude = completedLatitude;
          openHouse.completed_longitude = completedLongitude;
        }
        if (syncData) {
          const index = syncData.findIndex(
            (item: any) => item.openHouse.uuid === openHouse.uuid
          );
          if (index !== -1) {
            syncData[index] = { openHouse, data };
          } else {
            syncData.push({ openHouse, data });
          }
        } else {
          syncData = [{ openHouse, data }];
        }
        const status = "active";
        let localActive = await getLocalData(`openHouses-${status}`);
        if (localActive) {
          const index = localActive?.openHouses?.findIndex(
            (item: any) => item.uuid === openHouse.uuid
          );
          if (index !== -1) {
            if (localActive.openHouses[index]) {
              localActive.openHouses.splice(index, 1);

              if (localActive.openHouses.length === 0) {
                await removeKey(`openHouses-${status}`);
              } else {
                await storeLocalData(`openHouses-${status}`, localActive);
              }
            }
          } else {
            await removeKey(`openHouses-${status}`);
          }
        }
        await storeLocalData(`syncData`, syncData);
        assertAccount();
        setShowOfflineCompleteModal(true);
        await queryClient.invalidateQueries(["openHouses"]);
        assertAccount();
        setOpenHouse(openHouse);
        navigation.goBack();
      } catch (error) {
        assertAccount();
        console.error("Error updating sync data:", error);
        Toast.show({
          type: "error",
          text1: "Error",
          text2: "An error occurred while ending your open house.",
        });
        navigation.goBack();
      }
    } else {
      await completeOpenHouse(
          openHouse.completed_at
            ? "/openhouse/editCompleted"
            : "/openhouse/complete",
          data
        )
        .then(async (res) => {
          assertAccount();
          Toast.show({
            type: "success",
            text1: openHouse.completed_at
              ? "Open House Updated"
              : "Open House Completed",
            text2: openHouse.completed_at
              ? "The open house has been successfully updated"
              : "The open house has been successfully completed.",
          });
          setOpenHouse({
            ...res.data?.data?.openHouse,
            propertyDetails: openHouse?.propertyDetails,
            host: openHouse?.host,
          });

          await queryClient.invalidateQueries(["openHouses"]);
          await queryClient.invalidateQueries(["dashboardStats"]);
          assertAccount();
          navigation.goBack();
        })
        .catch((error) => {
          assertAccount();
          console.error("Error completing open house:", error);
          Toast.show({
            type: "error",
            text1: "Error",
            text2: "An error occurred while ending your open house.",
          });
        });
    }
  };

  const onEndPressed = async () => {
    navigation.navigate("EndOpenHouse", {
      openHouse: openHouse,
      onSubmit: (closedData: ClosedData) => closeOpenHouse(closedData),
      attendeeCount: openHouse.completed_at
        ? openHouse?.manual_attendee_count
        : attendees.length,
    });
  };

  const downloadSignInSheet = () => {
    setShowDownloadLoading(true);
    downloadSignInMutation.mutate({
      qrCodeUrl: `${NAVIGATION_PREFIX}/?id=${route?.params?.openHouse?.check_in_key
        }&team=${encodeURIComponent(
          user?.organization_info?.team || ""
        )}&phone=${encodeURIComponent(user?.managed_phone_number || "")}`,
      userUuid: user?.uuid,
      streetAddress: openHouse?.propertyDetails?.street_address,
      displayAgentLabel:
        openHouse?.host?.first_name + " " + openHouse?.host?.last_name,
      agentBrivityUuid: openHouse?.host?.brivity_person_uuid,
      onBeforeShare: () => {
        setShowDownloadLoading(false);
      },
    });
  };

  useEffect(() => {
    if (downloadSignInMutation.isSuccess) {
      moreOptionsModalRef.current?.hide();
    }
  }, [downloadSignInMutation.isSuccess]);

  useEffect(() => {
    if (downloadSignInMutation.isError) {
      setShowDownloadLoading(false);
    }
  }, [downloadSignInMutation.isError]);

  const LoadingModal = useMemo(
    () => (
      <Modal
        visible={showDownloadLoading}
        transparent={true}
        animationType="fade"
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
          }}
          pointerEvents={Platform.OS === "web" ? "auto" : "none"}
        >
          <View
            style={{
              backgroundColor: "white",
              padding: 30,
              borderRadius: 10,
              alignItems: "center",
              minWidth: 200,
            }}
          >
            <Text
              style={{
                marginBottom: 15,
                fontSize: 16,
                fontWeight: "600",
                textAlign: "center",
              }}
            >
              Creating Sign-In Sheet
            </Text>
            <ActivityIndicator size="large" color="#75c2c3" />
            <Text
              style={{
                marginTop: 5,
                fontSize: 14,
                color: "#666",
                textAlign: "center",
              }}
            >
              Just a moment your download is starting soon.
            </Text>
          </View>
        </View>
      </Modal>
    ),
    [showDownloadLoading]
  );

  const baseOptions: MoreOptionsModalOption[] = [
    {
      id: "edit",
      icon: "edit-2",
      text: "Edit Open House",
      onPress: () => {
        openHouse?.completed_at
          ? onEndPressed()
          : navigation.navigate("EditOpenHouse", {
            openHouse: { ...openHouse, tags: attendeesAndTags?.tags },
            onDeletePressed: () => {
              setShowConfirmDeleteOpenHouseModal(true);
              navigation.goBack();
            },
            onSave: (data: OpenHouse) => {
              let updatedOpenHouse = {
                ..._openHouse,
                ...data,
                propertyDetails: data?.propertyDetails,
                host: openHouse?.host,
              };
              navigation.setParams({
                openHouse: updatedOpenHouse,
              });
              setOpenHouse(updatedOpenHouse);
              getAllAttendees(route?.params?.openHouse?.uuid);
            },
          });
      },
      disabled: Boolean(openHouse?.is_active ?? false),
      redText: false,
    },
    {
      id: "download",
      icon: "download",
      text: "Download Sign-In Sheet",
      onPress: () => {
        downloadSignInSheet();
      },
      disabled: Boolean(
        !openHouse?.check_in_key ||
        openHouse?.completed_at ||
        downloadSignInMutation.isLoading
      ),
      redText: false,
    },
    {
      id: "copy",
      icon: "copy",
      text: "Copy Form Link",
      onPress: async () => {
        const url = `${NAVIGATION_PREFIX}/?id=${route?.params?.openHouse?.check_in_key
          }&team=${encodeURIComponent(
            user?.organization_info?.team || ""
          )}&phone=${encodeURIComponent(user?.managed_phone_number || "")}`;
        if (Platform.OS === "web") {
          navigator.clipboard.writeText(url);
        } else {
          await Clipboard.setStringAsync(url);
        }
        Toast.show({
          type: "success",
          text1: "Link Copied",
          text2: "The sign-in form link has been copied to your clipboard",
        });
      },
      disabled: Boolean(!openHouse?.check_in_key || openHouse?.completed_at),
      redText: false,
    },
    {
      id: "delete",
      icon: "trash-2",
      text: "Delete Open House",
      onPress: () => {
        handleDelete();
      },
      disabled: Boolean(openHouse?.is_active ?? false),
      redText: true,
    },
  ];

  const handleDelete = () => {
    if (attendees.length > 0) {
      setShowCannotDeleteModal(true);
    } else if (pingTime === null) {
      setShowNoConnectionModal(true);
    } else {
      onDeleteOpenHousePressed();
    }
  };

  const onStartPressed = async () => {
    // Try to get user's current location (non-blocking - will continue even if it fails)
    let startLatitude: number | null = null;
    let startLongitude: number | null = null;

    try {
      const location = await getCurrentLocation();
      if (location) {
        startLatitude = location.latitude;
        startLongitude = location.longitude;
      }
    } catch (error) {
      // Silently fail - location is optional, don't block starting the open house
      console.log("Could not get location for open house start:", error);
    }

    const deviceInfo = await getDeviceInfo();

    if (pingTime === null) {
      const upcoming = "upcoming";
      const active = "active";
      let localUpcoming = await getLocalData(`openHouses-${upcoming}`);
      let localActive = await getLocalData(`openHouses-${active}`);
      if (localUpcoming) {
        const index = localUpcoming?.openHouses?.findIndex(
          (item: any) => item.uuid === openHouse.uuid
        );
        if (index !== -1) {
          if (localUpcoming.openHouses[index]) {
            let openHouse = localUpcoming.openHouses[index];
            openHouse.is_active = true;
            openHouse.active_at = new Date().toISOString();
            // Store location in offline mode if available
            if (startLatitude !== null && startLongitude !== null) {
              openHouse.start_latitude = startLatitude;
              openHouse.start_longitude = startLongitude;
            }
            localActive.openHouses.push(openHouse);
            localUpcoming.openHouses.splice(index, 1);
            await storeLocalData(`openHouses-${upcoming}`, localUpcoming);
            await storeLocalData(`openHouses-${active}`, {
              openHouses: [openHouse],
              page: 1,
            });
            const localStarted = await getLocalData(`@startedUuids`);
            if (localStarted) {
              localStarted.push({
                uuid: openHouse.uuid,
                active_at: openHouse.active_at,
                startLatitude: startLatitude,
                startLongitude: startLongitude,
                deviceInfo,
              });
              await storeLocalData(`@startedUuids`, localStarted);
            } else {
              await storeLocalData(`@startedUuids`, [
                {
                  uuid: openHouse.uuid,
                  active_at: openHouse.active_at,
                  startLatitude: startLatitude,
                  startLongitude: startLongitude,
                  deviceInfo,
                },
              ]);
            }
            setOpenHouse({
              ...openHouse,
              propertyDetails: openHouse?.propertyDetails,
            });
            onCheckInModePressed();
            setTimeout(async () => {
              await queryClient.invalidateQueries(["openHouses"]);
            }, 1500);
          }
        }
      }
    } else {
      await openhouseAPI
        .post("/openhouse/start", {
          uuid: openHouse?.propertyDetails?.open_house_uuid || openHouse?.uuid,
          startLatitude: startLatitude,
          startLongitude: startLongitude,
          deviceInfo,
        })
        .then(async (response) => {
          setOpenHouse({
            ...response.data?.data,
            propertyDetails: openHouse?.propertyDetails,
            host: openHouse?.host,
          });
          onCheckInModePressed();
          await queryClient.invalidateQueries(["openHouses"]);
          await queryClient.invalidateQueries(["dashboardStats"]);
          queryClient.fetchQuery([
            "openHouses",
            "active",
            openHouse?.host?.uuid,
          ]);
          queryClient.fetchQuery([
            "openHouses",
            "current",
            openHouse?.host?.uuid,
          ]);
        });
    }
  };

  const onCheckInModePressed = () => {
    navigation.navigate("OpenHouseCheckIn", {
      openHouseId: route.params?.openHouseId,
      openHouse: route.params.openHouse,
    });
  };

  const onUpdateOpenHouse = (updateMutation: any, data: OpenHouse) => {
    setShowOpenHouseModal(false);

    updateMutation
      .mutateAsync({ id: route.params.openHouseId, updatedData: data })
      .then(() => {
        setTimeout(() => {
          setShowOpenHouseUpdatedModal(true);
        }, 500);
      });
  };

  const onDeleteOpenHousePressed = () => {
    setShowConfirmDeleteOpenHouseModal(true);
  };

  const OfflineCompleteModal = () => (
    <Popup
      show={showOfflineCompleteModal}
      header={
        <Text size={16} weight="Bold" lightColor={Colors.nuetral_grey_700}>
          You're Offline
        </Text>
      }
      body={
        <>
          <Text
            size={14}
            lightColor={Colors.nuetral_grey_700}
            style={{ textAlign: "center" }}
          >
            It appears your system is offline. In order to sync your data and
            leads to Brivity CRM you will need to have a network connection.
          </Text>
          <Text
            size={14}
            lightColor={Colors.nuetral_grey_700}
            weight="Bold"
            style={{ textAlign: "center", marginTop: 16 }}
          >
            Pending changes are saved on this device. Sign back in to the same
            account to sync them. Deleting the app will erase unsynced data.
          </Text>
        </>
      }
      footer={
        <View
          lightColor={Colors.base_colors_white}
          darkColor={Colors.nuetral_grey_700}
          style={{
            height: 55,
            flexDirection: "row",
            borderTopColor: Colors.tinted_grey_300,
            borderTopWidth: 1,
            paddingVertical: 8,
          }}
        >
          <Pressable
            style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
            onPress={() => setShowOfflineCompleteModal(false)}
          >
            <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
              Close
            </Text>
          </Pressable>
        </View>
      }
    ></Popup>
  );

  const ConfirmDeleteModal = () => (
    <Popup
      show={showConfirmDeleteOpenHouseModal}
      header={
        <Text
          style={{ textAlign: "center" }}
          size={16}
          weight="Bold"
          lightColor={Colors.nuetral_grey_700}
        >
          Are you sure you want to delete this open house?
        </Text>
      }
      footer={
        <View
          lightColor={Colors.base_colors_white}
          darkColor={Colors.nuetral_grey_700}
          style={{
            height: 55,
            flexDirection: "row",
            borderTopColor: Colors.tinted_grey_300,
            borderTopWidth: 1,
            paddingVertical: 8,
          }}
        >
          <Pressable
            style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
            onPress={() => setShowConfirmDeleteOpenHouseModal(false)}
          >
            <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
              Cancel
            </Text>
          </Pressable>
          <Pressable
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              borderLeftColor: Colors.tinted_grey_300,
              borderLeftWidth: 1,
            }}
            onPress={() => deleteOpenHouse()}
          >
            <Text lightColor={Colors.primary_500} weight="Bold">
              Delete
            </Text>
          </Pressable>
        </View>
      }
    ></Popup>
  );

  const deleteOpenHouse = async () => {
    setShowConfirmDeleteOpenHouseModal(false);
    await _deleteOpenHouse(openHouse?.uuid);
    setTimeout(async () => {
      await queryClient.invalidateQueries(["openHouses"]);
      await queryClient.invalidateQueries(["dashboardStats"]);
      navigation.goBack();
    }, 300);
  };

  const CannotDeleteModal = () => (
    <Popup
      show={showCannotDeleteModal}
      header={
        <Text
          style={{ textAlign: "center" }}
          size={16}
          weight="Bold"
          lightColor={Colors.nuetral_grey_700}
        >
          Cannot Delete Open House
        </Text>
      }
      body={
        <Text
          size={14}
          lightColor={Colors.nuetral_grey_700}
          weight="SemiBold"
          style={{ textAlign: "center" }}
        >
          This Open House cannot be deleted because it has attendees. Only Open
          Houses with no attendees can be deleted.
        </Text>
      }
      footer={
        <View
          lightColor={Colors.base_colors_white}
          darkColor={Colors.nuetral_grey_700}
          style={{
            height: 55,
            flexDirection: "row",
            borderTopColor: Colors.tinted_grey_300,
            borderTopWidth: 1,
            paddingVertical: 8,
          }}
        >
          <Pressable
            style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
            onPress={() => setShowCannotDeleteModal(false)}
          >
            <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
              Close
            </Text>
          </Pressable>
        </View>
      }
    ></Popup>
  );

  const NoConnectionModal = () => (
    <Popup
      show={showNoConnectionModal}
      header={
        <Text
          style={{ textAlign: "center" }}
          size={16}
          weight="Bold"
          lightColor={Colors.nuetral_grey_700}
        >
          You're Offline
        </Text>
      }
      body={
        <Text
          size={14}
          lightColor={Colors.nuetral_grey_700}
          weight="SemiBold"
          style={{ textAlign: "center" }}
        >
          It appears your system is offline. In order to delete open houses you
          will need to have a network connection.
        </Text>
      }
      footer={
        <View
          lightColor={Colors.base_colors_white}
          darkColor={Colors.nuetral_grey_700}
          style={{
            height: 55,
            flexDirection: "row",
            borderTopColor: Colors.tinted_grey_300,
            borderTopWidth: 1,
            paddingVertical: 8,
          }}
        >
          <Pressable
            style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
            onPress={() => setShowNoConnectionModal(false)}
          >
            <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
              Close
            </Text>
          </Pressable>
        </View>
      }
    ></Popup>
  );

  return (
    <View
      lightColor={Colors.background}
      darkColor={Colors.nuetral_grey_600}
      style={{ flex: 1 }}
    >
      <PageHeader
        leftComponent={<BackButton />}
        offlineBannerMessage="Lead sync is unavailable in offline mode."
        centerComponent={
          <PageTitleContainer>
            <PageTitleAddress
              address={`${openHouse?.propertyDetails?.street_address} ${openHouse?.propertyDetails?.city} ${openHouse?.propertyDetails?.state}, ${openHouse?.propertyDetails?.postal_code}`}
            />
            <PageTitleDate openHouse={openHouse} isTablet={isTablet} />
          </PageTitleContainer>
        }
        rightComponent={
          <MoreOptionsButton
            onPress={() => {
              moreOptionsModalRef.current?.show(baseOptions);
            }}
          />
        }
        bottomBorder
      />
      <KeyboardAwareScrollView
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 85 }}
      >
        <ManualSyncButton uuid={_openHouse?.uuid} onSynced={async () => { await getPing(); await refetchAttendees(); }} />
        <OpenHouseInformation isTablet={isTablet}>
          <OpenHouseInformationTopContainer>
            <OpenHouseInformationTopSection>
              <OpenHouseInformationLabel
                label="Host:"
                value={` ${openHouse?.host?.first_name} ${openHouse?.host?.last_name}`}
                isTablet={isTablet}
              />
              <OpenHouseInformationLabel
                label="Registration Form:"
                value={formType}
                isTablet={isTablet}
              />
              <OpenHouseTags
                tags={attendeesAndTags?.tags}
                isTablet={isTablet}
              />
            </OpenHouseInformationTopSection>
            {isTablet && (
              <OpenHouseInformationButtonContainer isTablet={isTablet}>
                <>
                  {openHouse?.active_at === null &&
                    !openHouse?.is_active &&
                    openHouse?.completed_at === null && (
                      <Button
                        text="Start"
                        textColor={Colors.base_colors_white}
                        lightColor={Colors.primary_500}
                        darkColor={Colors.primary_500}
                        icon={"clock"}
                        iconColor={Colors.base_colors_white}
                        onPress={() => {
                          onStartPressed();
                        }}
                      />
                    )}
                  {openHouse?.is_active && (
                    <>
                      <Button
                        text="Check-in Mode"
                        textColor={Colors.primary_500}
                        borderColor={Colors.primary_500}
                        transparent={true}
                        onPress={() => {
                          onCheckInModePressed();
                        }}
                      />
                      <Button
                        text="End"
                        textColor={Colors.base_colors_white}
                        lightColor={Colors.primary_500}
                        darkColor={Colors.primary_500}
                        icon={"clock"}
                        iconColor={Colors.base_colors_white}
                        onPress={() => {
                          onEndPressed();
                        }}
                      />
                    </>
                  )}
                  {openHouse?.completed_at && (
                    <Text
                      size={16}
                      weight="Bold"
                      lightColor={Colors.nuetral_grey_700}
                    >
                      {completionPending ? "Sync Required" : "Completed"}
                    </Text>
                  )}
                </>
              </OpenHouseInformationButtonContainer>
            )}
          </OpenHouseInformationTopContainer>

          <OpenHouseNotes notes={openHouse?.instructions} isTablet={isTablet} />

          {!isTablet && (
            <View
              transparent
              style={{
                alignItems:
                  openHouse?.active_at === null && !openHouse.is_active
                    ? "flex-end"
                    : "stretch",
              }}
            >
              <OpenHouseInformationButtonContainer isTablet={isTablet}>
                <>
                  {openHouse?.active_at === null &&
                    !openHouse.is_active &&
                    openHouse?.completed_at === null && (
                      <Button
                        text="Start"
                        textColor={Colors.base_colors_white}
                        lightColor={Colors.primary_500}
                        darkColor={Colors.primary_500}
                        icon={"clock"}
                        iconColor={Colors.base_colors_white}
                        onPress={() => {
                          onStartPressed();
                        }}
                      />
                    )}
                  {openHouse?.is_active && (
                    <>
                      <Button
                        text="Check-in Mode"
                        textColor={Colors.primary_500}
                        borderColor={Colors.primary_500}
                        transparent={true}
                        onPress={() => {
                          onCheckInModePressed();
                        }}
                      />
                      <Button
                        text="End"
                        textColor={Colors.base_colors_white}
                        lightColor={Colors.primary_500}
                        darkColor={Colors.primary_500}
                        icon={"clock"}
                        iconColor={Colors.base_colors_white}
                        onPress={() => {
                          onEndPressed();
                        }}
                      />
                    </>
                  )}
                  {openHouse?.completed_at && (
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        width: "100%",
                      }}
                    >
                      <Text
                        size={16}
                        weight="Bold"
                        lightColor={Colors.nuetral_grey_700}
                      >
                        {completionPending ? "Sync Required" : "Completed"}
                      </Text>
                    </View>
                  )}
                </>
              </OpenHouseInformationButtonContainer>
            </View>
          )}
        </OpenHouseInformation>
        <MainSection isTablet={isTablet}>
          <StatSection>
            <StatsBlock
              style={{ flex: 1 }}
              number={
                openHouse?.completed_at
                  ? calculateHoursBetween(
                    openHouse?.start_time,
                    openHouse?.end_time
                  ) >= 0
                    ? calculateHoursBetween(
                      openHouse?.start_time,
                      openHouse?.end_time
                    )
                    : 0
                  : 0
              }
              text={"Hours"}
            />
            <Line orientation={Orientation.Vertical} />
            <StatsBlock
              style={{ flex: 1 }}
              number={
                openHouse?.is_active
                  ? attendees?.length
                  : openHouse?.manual_attendee_count
              }
              text={"Attendees"}
            />
            <Line orientation={Orientation.Vertical} />
            <StatsBlock
              style={{ flex: 1 }}
              number={attendees?.length}
              text={"Leads"}
            />
          </StatSection>
          <ListSection>
            <ListHeaderSection>
              <ListHeader text="Name" width={isTablet ? 0.3 : 0.5} />
              <ListHeader text="Intent" width={isTablet ? 0.2 : 0.5} />
              {isTablet && (
                <>
                  <ListHeader text="Email" width={0.3} />
                  <ListHeader text="Phone Number" width={0.3} />
                </>
              )}
              <ListHeader text="" width={0.005} />
            </ListHeaderSection>
            <>
              <LeadList
                data={attendees}
                pingTime={pingTime}
                isTablet={isTablet}
                uuid={openHouse?.uuid}
                localAttendees={localAttendees}
                startTime={openHouse?.start_time}
                completedAt={openHouse?.completed_at}
                isFetched={isFetched}
                uuid={openHouse?.uuid}
                user={user}
                syncing={syncing}
              />
            </>
          </ListSection>
        </MainSection>
      </KeyboardAwareScrollView>
      <RefreshButton
        isFetching={isFetching}
        getAttendees={async () => await getAllAttendees(openHouse?.uuid)}
        pingTime={pingTime}
      />

      {/* Modals */}
      <CreateEditOpenHouse
        data={openHouse}
        show={showOpenHouseModal}
        isEditMode={true}
        onSubmit={(data) => onUpdateOpenHouse(updateMutation, data)}
        onDeletePressed={() => onDeleteOpenHousePressed()}
        onClosed={() => setShowOpenHouseModal(false)}
        floatingComponent={
          showConfirmDeleteOpenHouseModal && <ConfirmDeleteModal />
        }
      />
      {showOfflineCompleteModal && <OfflineCompleteModal />}
      {showConfirmDeleteOpenHouseModal && <ConfirmDeleteModal />}
      {showCannotDeleteModal && <CannotDeleteModal />}
      {showNoConnectionModal && <NoConnectionModal />}
      <MoreOptionsModal
        ref={moreOptionsModalRef}
        options={baseOptions}
        top={Platform.OS === "web" ? 40 : isTablet ? 60 : 95}
        right={Platform.OS === "web" ? 65 : isTablet ? 20 : 20}
      />
      {LoadingModal}
    </View>
  );
};

export default OpenHouseDetails;
